import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { APP_ROUTES } from "../../../config/appRoutes";
import CampaignIcon from '@mui/icons-material/Campaign';
import { getCampaignsThunk } from "../campaignThunk";
import { useDispatch } from "react-redux";
import { resetActiveBlock } from "../campaignSlice";
import { getTemplateThunk } from '../../templates/templatesThunk';
import { QR, QrActionButton } from "../../qrCode/components/qrUi";

// Header card for the campaign pages (same look as the QR module).
// Optional: `stats` (cards shown inside the header) and `children` (page content inside the card).
export default function PageButtons({ stats = [], children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const [activePath, setActivePath] = useState(location.pathname);
    useEffect(() => {
        setActivePath(location.pathname);
    }, [location.pathname]);

    const handleClick = (path) => {
        setActivePath(path);
        navigate(path);
        dispatch(resetActiveBlock());
    };

    useEffect(() => {
        dispatch(getCampaignsThunk());
        dispatch(getTemplateThunk());
    }, [dispatch]);



    return (
        <Box
            sx={{
                position: "relative",
                overflow: "hidden",
                p: { xs: 1.5, sm: 2, md: 2.5 },
                borderRadius: 3,
                border: `1px solid ${QR.border}`,
                bgcolor: "#fff",
                boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
            }}
        >
            {/* Top row: title + toggle + sync */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar
                        variant="rounded"
                        sx={{
                            width: { xs: 40, sm: 46 },
                            height: { xs: 40, sm: 46 },
                            borderRadius: 2.5,
                            color: "#fff",
                            background: `linear-gradient(135deg, ${QR.main}, ${QR.light})`,
                            boxShadow: `0 8px 18px -8px ${QR.main}`,
                        }}
                    >
                        <CampaignIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight={800} color="#0f172a" lineHeight={1.2} sx={{ fontSize: { xs: 17, sm: 20 } }}>
                            Outbound Campaigns
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
                            Broadcast WhatsApp templates to your contacts and track delivery
                        </Typography>
                    </Box>
                </Stack>

                {/* One button that changes with the page: list → "Create Campaign", create → "Back to List" */}
                <Box sx={{ width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}>
                {activePath === APP_ROUTES.CAMPAIGN.CREATION ? (
                    <QrActionButton
                        icon={<ArrowBackIcon sx={{ fontSize: 17 }} />}
                        label="Back to List"
                        variant="solid"
                        color="#fc8006" // logo orange
                        gradientTo="#fb923c"
                        onClick={() => handleClick(APP_ROUTES.CAMPAIGN.LIST)}
                    />
                ) : (
                    <QrActionButton
                        variant="solid"
                        color={QR.blue}
                        gradientTo="#3b82f6"
                        icon={<AddCircleOutlineIcon sx={{ fontSize: 17 }} />}
                        label="Create Campaign"
                        onClick={() => handleClick(APP_ROUTES.CAMPAIGN.CREATION)}
                    />
                )}
                </Box>
            </Box>

            {/* Stats cards inside the header (only when the page passes stats) */}
            {stats.length > 0 && (
                <Box
                    sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: `1px solid ${QR.border}`,
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", lg: `repeat(${stats.length}, minmax(0, 1fr))` },
                        gap: 1.5,
                    }}
                >
                    {stats.map((s, idx) => (
                        <Stack
                            key={s.label}
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                                // phones: an odd last card spans both columns
                                gridColumn: { xs: stats.length % 2 === 1 && idx === stats.length - 1 ? "span 2" : "auto", sm: "auto" },
                                p: { xs: 1.25, sm: 1.5 },
                                borderRadius: 3,
                                bgcolor: "#fff",
                                border: `1px solid ${QR.border}`,
                                borderTop: `3px solid ${s.color}`,
                                boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
                            }}
                        >
                            <Avatar variant="rounded" sx={{ width: 40, height: 40, borderRadius: 2, color: s.color, bgcolor: `${s.color}14` }}>
                                {s.icon}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap display="block">
                                    {s.label}
                                </Typography>
                                <Typography variant="h6" fontWeight={800} lineHeight={1.2} color="#0f172a">
                                    {s.value}
                                </Typography>
                            </Box>
                        </Stack>
                    ))}
                </Box>
            )}

            {/* Page content inside the same card (e.g. the campaign table) */}
            {children && (
                <Box sx={{ mt: 2.5, pt: 0.5, borderTop: `1px solid ${QR.border}` }}>
                    {children}
                </Box>
            )}
        </Box>
    );
}
